import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  message,
  Row,
  Col,
  Button,
  Space,
  TimePicker,
  InputNumber,
  Modal,
  Skeleton,
  Switch,
  Tooltip
} from 'antd';
import { PlusOutlined, MinusOutlined, ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import EditableDrawer from 'components/EditableDrawer';
import CascadingAddressSelector from 'components/CascadingAddressSelector';
import { useMutation, useQuery } from '@apollo/client';
import { SAVE_COOPERATION_WAREHOUSE } from '../graphql/mutations/cooperationWarehouseMutations';
import { getWeekdaysOptions } from '../../appointment/types/PickupAppointmentType';
import { useWarehouseContext } from "../../../../contexts/WarehouseContext";
import dayjs from 'dayjs';
import { allChannelLiteResponse, GET_ALL_CHANNEL_LITE, ChannelLiteType } from "../../../../graphql/queries/getChannelAll";
import { useVehicleTypes } from '../../../../hooks/useVehicleTypeOptions';
import { VEHICLE_TYPE_STATUS_ENABLED } from '../../../../graphql/queries/getAllVehicleTypes';

// 添加样式来调整按钮宽度和必填星号
const drawerStyles = `
  .cooperation-warehouse-drawer .ant-drawer-footer .ant-btn {
    width: 100px !important;
  }
  
  .required-field-label::after {
    display: inline-block;
    margin-inline-start: 4px;
    color: #ff4d4f;
    font-size: 14px;
    font-family: SimSun, sans-serif;
    line-height: 1;
    content: "*";
  }
  
  /* 弹窗按钮居中样式 */
  .custom-modal .ant-modal-footer {
    text-align: center !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    padding-top: 8px !important;
    padding-bottom: 36px !important;
  }
  
  .custom-modal .ant-modal-footer .ant-btn {
    width: 100px !important;
    margin: 0 4px !important;
  }
`;

interface WarehouseFormDrawerProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (values: any) => void;
  initialValues?: any;
  mode: 'create' | 'edit' | 'view';
  statusOptions: { label: string; value: number }[];
}

const inputStyle = { width: '100%' };
const selectStyle = { width: '100%' };
const labelStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  height: '100%'
};
const labelStyleCustomer = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  height: '100%',
  marginTop: '0px',
  marginBottom: '0px',
  lineHeight: '0.9'
};
const viewLabelStyle = { minWidth: '165px' }
interface OperateOption {
  value: string;
  label: string;
}

const CooperationWarehouseFormDrawer: React.FC<WarehouseFormDrawerProps> = ({
  visible,
  onClose,
  onSubmit,
  initialValues,
  mode,
  statusOptions,
}) => {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalConfirming, setIsModalConfirming] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // 多行数据使用独立 state 管理，不使用 form 索引
  const [aliasList, setAliasList] = useState<string[]>(['']);
  const [relatedAddressList, setRelatedAddressList] = useState<Array<{ address1: string, address2: string }>>([{ address1: '', address2: '' }]);
  // 控制 channelId 字段显示的状态
  const [standingPickupValue, setStandingPickupValue] = useState<boolean | undefined>(undefined);

  const { currentWarehouse: warehouse } = useWarehouseContext();

  const [channelOptions, setChannelOptions] = useState<OperateOption[]>([]);
  // 获取渠道列表（轻量级，仅 id + channelName）
  const { data: channelData, loading: channelLoading, error: channelError } = useQuery<allChannelLiteResponse>(GET_ALL_CHANNEL_LITE)

  // 获取车型列表
  const { vehicleTypes, loading: vehicleLoading } = useVehicleTypes();

  const [saveWarehouse] = useMutation(SAVE_COOPERATION_WAREHOUSE, {
    // 阻止Apollo自动更新缓存，避免失败时污染列表数据
    update: () => { }, // 空函数阻止缓存更新
    fetchPolicy: 'no-cache' // 不使用缓存
  });

  // 处理渠道选项
  useEffect(() => {
    if (channelError) {
      message.error('Failed to load channel list: ' + channelError.message);
      setChannelOptions([]);
      return;
    }

    if (channelData?.getAllChannelsLite) {
      const channels = channelData.getAllChannelsLite;
      const options = channels
        .filter((channel: ChannelLiteType) => channel && channel.id && channel.channelName) // 过滤掉无效数据
        .map((channel: ChannelLiteType) => ({
          value: channel.id.toString(),
          label: channel.channelName
        }));
      setChannelOptions(options);
    } else if (!channelLoading) {
      // 如果没有数据且不在加载中，设置为空数组
      setChannelOptions([]);
    }
  }, [channelData, channelError, channelLoading]);

  // 处理集散中心选项
  const hubOptions = React.useMemo(() => {
    const options: { label: string; value: string | number }[] = [];

    // 优先从 initialValues 中获取当前站点的集散中心信息（用于编辑/查看模式回显）
    if (initialValues?.warehouseId) {
      options.push({
        label: initialValues.warehouseName || '-',
        value: initialValues.warehouseId
      });
    }

    // 从上下文获取当前活跃的集散中心信息（用于新增模式或回补显示）
    if (warehouse?.id) {
      const contextWarehouseId = isNaN(Number(warehouse.id)) ? warehouse.id : Number(warehouse.id);
      const exists = options.some(opt => opt.value === contextWarehouseId);
      if (!exists) {
        options.push({
          label: warehouse.name || '-',
          value: contextWarehouseId
        });
      }
    }

    return options;
  }, [warehouse, initialValues]);

  // 处理车型选项（全量，用于查看模式展示禁用车型名称）
  const vehicleModelOptions = React.useMemo(() => {
    return (vehicleTypes || []).map(vt => ({
      value: vt.id,
      label: vt.nameEn,
    }));
  }, [vehicleTypes]);

  // 处理车型选项（仅启用，用于编辑/创建模式的下拉选择）
  const vehicleModelActiveOptions = React.useMemo(() => {
    return (vehicleTypes || [])
      .filter(vt => vt.status === VEHICLE_TYPE_STATUS_ENABLED)
      .map(vt => ({
        value: vt.id,
        label: vt.nameEn,
      }));
  }, [vehicleTypes]);

  // 弹窗状态管理
  const [modalState, setModalState] = useState<{
    visible: boolean;
    type: 'same' | 'similar' | null;
    data: any;
  }>({
    visible: false,
    type: null,
    data: null
  });

  // 抽屉打开时立即显示加载状态
  useEffect(() => {
    setIsLoading(visible);
  }, [visible]);

  useEffect(() => {
    if (!visible || mode === 'view' || vehicleModelActiveOptions.length === 0) return;
    const currentVehicleTypeId = form.getFieldValue('vehicleTypeId');
    if (currentVehicleTypeId != null && !vehicleModelActiveOptions.some(opt => opt.value === currentVehicleTypeId)) {
      form.setFieldsValue({ vehicleTypeId: undefined });
    }
  }, [vehicleModelActiveOptions, visible, mode, form]);

  // 当 initialValues 从空变为有数据时，结束加载状态并设置表单
  useEffect(() => {
    if (visible && initialValues) {
      setIsLoading(false);
    }
    if (visible && mode !== 'view') {
      form.resetFields();

      if (initialValues && mode === 'edit') {
        // 设置 standingPickup 状态
        setStandingPickupValue(initialValues.standingPickup);

        // 设置多行数据 - 过滤掉 __typename 属性
        if (initialValues.aliasList && Array.isArray(initialValues.aliasList)) {
          setAliasList(initialValues.aliasList.length > 0 ? initialValues.aliasList : ['']);
        } else {
          setAliasList(['']);
        }

        if (initialValues.relatedAddressList && Array.isArray(initialValues.relatedAddressList)) {
          const cleanAddressList = initialValues.relatedAddressList.length > 0
            ? initialValues.relatedAddressList.map(({ __typename, ...address }: any) => address)
            : [{ address1: '', address2: '' }];
          setRelatedAddressList(cleanAddressList);
        } else {
          setRelatedAddressList([{ address1: '', address2: '' }]);
        }

        // 设置表单值，包括 contactPersonList 的 inBlacklist 字段
        setTimeout(() => {
          const allValues = {
            id: initialValues.id,
            name: initialValues.name,
            type: initialValues.type,
            warehouseId: initialValues.warehouseId,
            status: initialValues.status,
            addressLine1: initialValues.addressLine1,
            addressLine2: initialValues.addressLine2,
            country: initialValues.country,
            state: initialValues.state,
            city: initialValues.city,
            zipcode: initialValues.zipcode,
            latitude: initialValues.latitude,
            longitude: initialValues.longitude,
            email: initialValues.email,
            workWeekdayList: initialValues.workWeekdayList,
            workStartTime: initialValues.workStartTime ? (dayjs.isDayjs(initialValues.workStartTime) ? initialValues.workStartTime : dayjs(initialValues.workStartTime, 'HH:mm')) : null,
            workEndTime: initialValues.workEndTime ? (dayjs.isDayjs(initialValues.workEndTime) ? initialValues.workEndTime : dayjs(initialValues.workEndTime, 'HH:mm')) : null,
            contactPersonList: initialValues.contactPersonList && initialValues.contactPersonList.length > 0
              ? initialValues.contactPersonList.map(({ __typename, ...contact }: any) => ({
                ...contact,
                inBlacklist: contact.inBlacklist || false
              }))
              : [{ name: '', phone: '', countryCode: 1, extension: '', inBlacklist: false }],
            remark: initialValues.remark,
            standingPickup: initialValues.standingPickup,
            channelId: initialValues.channelId,
          };

          form.setFieldsValue(allValues);
        }, 100);

      } else {
        form.setFieldsValue({
          status: 1,
          warehouseId: warehouse?.id ? (isNaN(Number(warehouse.id)) ? warehouse.id : Number(warehouse.id)) : 0
          // standingPickup: 0,
        });
        setStandingPickupValue(undefined);
        setAliasList(['']);
        setRelatedAddressList([{ address1: '', address2: '' }]);
        setIsLoading(false);
      }
    }
  }, [visible, initialValues, form, mode]);

  // 多行数据操作函数
  const handleAddAlias = () => {
    const lastAlias = aliasList[aliasList.length - 1];
    if (!lastAlias || lastAlias.trim() === '') {
      message.error('Please fill in the current alias before adding a new one.');
      return;
    }
    setAliasList([...aliasList, '']);
  };

  const handleRemoveAlias = (index: number) => {
    if (aliasList.length > 1) {
      const newAliasList = aliasList.filter((_, i) => i !== index);
      setAliasList(newAliasList);
    }
  };

  const handleAliasChange = (index: number, value: string) => {
    const newAliasList = [...aliasList];
    newAliasList[index] = value;
    setAliasList(newAliasList);
  };

  const handleAddAddress = () => {
    const lastAddress = relatedAddressList[relatedAddressList.length - 1];
    if ((!lastAddress.address1 || lastAddress.address1.trim() === '') &&
      (!lastAddress.address2 || lastAddress.address2.trim() === '')) {
      message.error(t('pickup.cooperationWarehouse.messages.pleaseFillInAtLeastOneAddressFieldBeforeAddingANewOne') as string);
      return;
    }
    setRelatedAddressList([...relatedAddressList, { address1: '', address2: '' }]);
  };

  const handleRemoveAddress = (index: number) => {
    if (relatedAddressList.length > 1) {
      const newAddressList = relatedAddressList.filter((_, i) => i !== index);
      setRelatedAddressList(newAddressList);
    }
  };

  const handleAddressChange = (index: number, field: 'address1' | 'address2', value: string) => {
    const newAddressList = [...relatedAddressList];
    newAddressList[index] = { ...newAddressList[index], [field]: value };
    setRelatedAddressList(newAddressList);
  };

  // contactPersonList 现在由 Form.List 管理，不需要手动的事件处理函数

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      const values = await form.validateFields();

      // 构建提交数据，对所有文本字段进行去空格处理
      const cooperationWarehouseData = {
        // 基础字段
        name: values.name ? values.name.trim() : '',
        type: Number(values.type) || 1,
        warehouseId: Number(values.warehouseId),
        status: Number(values.status),
        addressLine1: values.addressLine1 ? values.addressLine1.trim() : '',
        addressLine2: values.addressLine2 ? values.addressLine2.trim() : '',
        country: values.country ? values.country.trim() : '',
        state: values.state ? values.state.trim() : '',
        city: values.city ? values.city.trim() : '',
        zipcode: values.zipcode ? values.zipcode.trim() : '',
        email: values.email ? values.email.trim() : '',
        // 如果是编辑模式，包含 id；如果是创建模式，不传 id
        ...(mode === 'edit' && initialValues?.id ? { id: initialValues.id } : {}),
        // 多行数据 - 过滤掉空行并去空格
        contactPersonList: values.contactPersonList?.filter((contact: any) =>
          contact.name && contact.name.trim() !== '' || contact.phone && contact.phone.trim() !== ''
        ).map((contact: any) => ({
          name: contact.name ? contact.name.trim() : '',
          phone: contact.phone ? contact.phone.trim() : '',
          countryCode: Number(contact.countryCode) || 1,
          extension: contact.extension ? contact.extension.trim() : '',
          inBlacklist: contact.inBlacklist || false
        })) || [],
        aliasList: aliasList.filter(alias => alias && alias.trim() !== '').map(alias => alias.trim()),
        relatedAddressList: relatedAddressList.filter(address =>
          address.address1 && address.address1.trim() !== '' || address.address2 && address.address2.trim() !== ''
        ).map(address => ({
          address1: address.address1 ? address.address1.trim() : '',
          address2: address.address2 ? address.address2.trim() : ''
        })),
        // 时间字段
        workStartTime: values.workStartTime ? values.workStartTime.format('HH:mm') : null,
        workEndTime: values.workEndTime ? values.workEndTime.format('HH:mm') : null,
        expectedPickupStartTime: values.expectedPickupStartTime ? values.expectedPickupStartTime.format('HH:mm') : null,
        expectedPickupEndTime: values.expectedPickupEndTime ? values.expectedPickupEndTime.format('HH:mm') : null,
        workWeekdayList: values.workWeekdayList || [],
        // 其他字段
        latitude: values.latitude ? Number(values.latitude) : null,
        longitude: values.longitude ? Number(values.longitude) : null,
        remark: values.remark ? values.remark.trim() : '',
        standingPickup: values.standingPickup ?? false,
        channelId: values.channelId ? Number(values.channelId) : null,
        relatedChannelIds: values.relatedChannelIds && Array.isArray(values.relatedChannelIds) && values.relatedChannelIds.length > 0
          ? values.relatedChannelIds.map((id: string | number) => Number(id))
          : null,
        vehicleTypeId: values.vehicleTypeId ? Number(values.vehicleTypeId) : null,
      };

      const response = await saveWarehouse({
        variables: {
          input: {
            cooperationWarehouse: cooperationWarehouseData,
            submitCount: 1
          }
        }
      });

      if (response.data?.saveCooperationWarehouse) {
        const result = response.data.saveCooperationWarehouse.result;
        const messageText = response.data.saveCooperationWarehouse.message;
        const originalRequest = response.data.saveCooperationWarehouse.originalRequest;
        const existingOrSimilar = response.data.saveCooperationWarehouse.existingOrSimilar;
        const submitCount = response.data.saveCooperationWarehouse.submitCount;

        if (result === 'success') {
          message.success(mode === 'create' ? t('pickup.cooperationWarehouse.messages.pickupSiteCreatedSuccessfully') as string : t('pickup.cooperationWarehouse.messages.pickupSiteUpdatedSuccessfully') as string);
          onSubmit(response.data);
        } else if (result === 'same') {
          // 相同地址 - 设置弹窗状态
          setModalState({
            visible: true,
            type: 'same',
            data: { existingOrSimilar, originalRequest, submitCount }
          });
        } else if (result === 'similar') {
          // 相似地址 - 设置弹窗状态
          setModalState({
            visible: true,
            type: 'similar',
            data: { existingOrSimilar, originalRequest, submitCount }
          });
        } else {
          message.error(messageText || t('pickup.cooperationWarehouse.messages.operationFailedPleaseTryAgain') as string);
        }
      }
    } catch (error: any) {
      // 区分表单校验错误和网络错误
      if (error.errorFields) {
        console.error('Form validation failed:', error);
      } else {
        // 网络错误或其他错误
        console.error('Submit failed:', error);
        const gqlErr = error?.graphQLErrors?.[0];
        const messageKey: string | undefined = gqlErr?.extensions?.messageKey;
        message.error(messageKey ? t(messageKey) as string : error.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const drawerTitle = mode === 'create' ? t('pickup.cooperationWarehouse.pageDetails.titles.createCooperationWarehouse') as string :
    mode === 'edit' ? t('pickup.cooperationWarehouse.pageDetails.titles.editCooperationWarehouse') as string :
      t('pickup.cooperationWarehouse.pageDetails.titles.cooperationWarehouseDetails') as string;

  // 在展示模式下切换到编辑模式
  const handleViewSubmit = () => {
    if (mode === 'view') {
      // 切换到编辑模式，重新加载数据到表单
      if (initialValues) {
        // 设置 standingPickup 状态
        setStandingPickupValue(initialValues.standingPickup);

        // 设置多行数据 - 过滤掉 __typename 属性
        setAliasList(initialValues.aliasList || ['']);

        if (initialValues.relatedAddressList && Array.isArray(initialValues.relatedAddressList)) {
          const cleanAddressList = initialValues.relatedAddressList.length > 0
            ? initialValues.relatedAddressList.map(({ __typename, ...address }: any) => address)
            : [{ address1: '', address2: '' }];
          setRelatedAddressList(cleanAddressList);
        } else {
          setRelatedAddressList([{ address1: '', address2: '' }]);
        }

        // contactPersonList 现在由 Form.List 管理，不需要手动设置



        // 设置表单值
        setTimeout(() => {
          const allValues = {
            id: initialValues.id,
            name: initialValues.name,
            type: initialValues.type,
            warehouseId: initialValues.warehouseId,
            status: initialValues.status,
            addressLine1: initialValues.addressLine1,
            addressLine2: initialValues.addressLine2,
            country: initialValues.country,
            state: initialValues.state,
            city: initialValues.city,
            zipcode: initialValues.zipcode,
            latitude: initialValues.latitude,
            longitude: initialValues.longitude,
            email: initialValues.email,
            workWeekdayList: initialValues.workWeekdayList,
            workStartTime: initialValues.workStartTime ? (dayjs.isDayjs(initialValues.workStartTime) ? initialValues.workStartTime : dayjs(initialValues.workStartTime, 'HH:mm')) : null,
            workEndTime: initialValues.workEndTime ? (dayjs.isDayjs(initialValues.workEndTime) ? initialValues.workEndTime : dayjs(initialValues.workEndTime, 'HH:mm')) : null,
            expectedPickupStartTime: initialValues.expectedPickupStartTime ? (dayjs.isDayjs(initialValues.expectedPickupStartTime) ? initialValues.expectedPickupStartTime : dayjs(initialValues.expectedPickupStartTime, 'HH:mm')) : null,
            expectedPickupEndTime: initialValues.expectedPickupEndTime ? (dayjs.isDayjs(initialValues.expectedPickupEndTime) ? initialValues.expectedPickupEndTime : dayjs(initialValues.expectedPickupEndTime, 'HH:mm')) : null,
            vehicleTypeId: vehicleModelActiveOptions.some(opt => opt.value === initialValues.vehicleTypeId)
              ? initialValues.vehicleTypeId
              : undefined,
            // 设置联系人数据
            contactPersonList: initialValues.contactPersonList && initialValues.contactPersonList.length > 0
              ? initialValues.contactPersonList.map(({ __typename, ...contact }: any) => ({
                ...contact,
                inBlacklist: contact.inBlacklist || false
              }))
              : [{ name: '', phone: '', countryCode: 1, extension: '', inBlacklist: false }],
            relatedChannelIds: initialValues.relatedChannelIds && Array.isArray(initialValues.relatedChannelIds)
              ? initialValues.relatedChannelIds.map((id: number) => id.toString())
              : undefined,
            remark: initialValues.remark,
          };

          form.setFieldsValue(allValues);
        }, 100);
      }
      // 通知父组件切换到编辑模式
      onSubmit({ _switchToEdit: true });
    }
  };

  // 获取状态文本
  const getStatusText = (status: number) => {
    const statusOption = statusOptions.find(option => option.value === status);
    return statusOption?.label || 'Unknown';
  };

  // 获取channel文本
  const getChannelText = (channelId: string | number | undefined) => {
    if (!channelId) return '-';
    const channelOption = channelOptions.find(option => option.value === channelId.toString());
    return channelOption?.label || 'Unknown';
  };

  // 获取多个channel文本
  const getChannelTexts = (channelIds: (string | number)[]) => {
    if (!channelIds || channelIds.length === 0) return '-';
    return channelIds.map(id => {
      const channelOption = channelOptions.find(option => option.value === id.toString());
      return channelOption?.label || 'Unknown';
    }).join('、');
  };

  // 格式化地址
  const formatAddress = (values: any) => {
    if (!values) return '-';
    const parts = [
      values.addressLine1,
      values.addressLine2,
      values.city,
      values.state
    ].filter(Boolean);
    return parts.join(',') + ' ' + values.zipcode || '-';
  };

  // 格式化联系人信息
  const formatContactPerson = (contactPersonList: any[]) => {
    if (!contactPersonList || contactPersonList.length === 0) return '-';
    const firstContact = contactPersonList[0];
    return firstContact.name || '-';
  };

  // 格式化电话号码
  const formatPhoneNumber = (contactPersonList: any[]) => {
    if (!contactPersonList || contactPersonList.length === 0) return '-';
    const firstContact = contactPersonList[0];
    if (firstContact?.phone) {
      const countryCode = firstContact.countryCode || '';
      const extension = firstContact.extension ? `-${firstContact.extension}` : '';
      return `+${countryCode} ${firstContact.phone}${extension}`;
    }
    return '-';
  };

  // 格式化别名列表
  const formatAliases = (aliasList: string[]) => {
    return aliasList?.join('、') || '-';
  };

  // 格式化关联地址
  const formatRelatedAddresses = (relatedAddressList: any[]) => {
    if (!relatedAddressList || relatedAddressList.length === 0) return '-';
    return relatedAddressList.map(addr => {
      const parts = [addr.address1, addr.address2].filter(Boolean);
      return parts.join(',');
    }).join('\n');
  };

  // 格式化工作时间
  const formatWorkTime = (values: any) => {
    if (!values || !values.workStartTime && !values.workEndTime && (!values.workWeekdayList || values.workWeekdayList.length === 0)) return '-';

    // 格式化工作日
    let weekdaysText = '';
    if (values.workWeekdayList && values.workWeekdayList.length > 0) {
      const weekdaysOptions = getWeekdaysOptions(t);
      weekdaysText = values.workWeekdayList
        .map((day: number) => {
          const weekdayOption = weekdaysOptions.find(option => option.value === day);
          return weekdayOption ? weekdayOption.label : '';
        })
        .filter(Boolean)
        .join(', ');
    }

    // 格式化时间
    let timeText = '';
    if (values.workStartTime || values.workEndTime) {
      const startTime = values.workStartTime || '';
      const endTime = values.workEndTime || '';
      timeText = `${startTime} - ${endTime}`;
    }

    // 组合显示
    if (weekdaysText && timeText) {
      return `${weekdaysText}: ${timeText}`;
    } else if (weekdaysText) {
      return weekdaysText;
    } else if (timeText) {
      return timeText;
    } else {
      return '-';
    }
  };

  // 弹窗处理函数
  const handleModalOk = async () => {
    if (modalState.type === 'similar' && modalState.data) {
      setIsModalConfirming(true);
      try {
        const { existingOrSimilar, originalRequest, submitCount } = modalState.data;

        // 过滤掉 originalRequest 中不属于 CooperationWarehouseInput 的字段
        const { isNew, continuousFailure, warehouseName, createdAt, updatedAt, __typename, ...filteredOriginalRequest } = originalRequest;

        // 过滤掉嵌套对象中的 __typename 字段
        if (filteredOriginalRequest.contactPersonList) {
          filteredOriginalRequest.contactPersonList = filteredOriginalRequest.contactPersonList.map((contact: any) => {
            const { __typename, ...filteredContact } = contact;
            return filteredContact;
          });
        }

        if (filteredOriginalRequest.relatedAddressList) {
          filteredOriginalRequest.relatedAddressList = filteredOriginalRequest.relatedAddressList.map((address: any) => {
            const { __typename, ...filteredAddress } = address;
            return filteredAddress;
          });
        }

        const confirmResponse = await saveWarehouse({
          variables: {
            input: {
              cooperationWarehouse: filteredOriginalRequest,
              submitCount: submitCount + 1,
              similarCooperationWarehouse: existingOrSimilar ? (() => {
                // 过滤掉 existingOrSimilar 中不属于 CooperationWarehouseInput 的字段
                const { isNew, continuousFailure, warehouseName, createdAt, updatedAt, __typename, ...filteredExistingOrSimilar } = existingOrSimilar;

                // 过滤掉嵌套对象中的 __typename 字段
                if (filteredExistingOrSimilar.contactPersonList) {
                  filteredExistingOrSimilar.contactPersonList = filteredExistingOrSimilar.contactPersonList.map((contact: any) => {
                    const { __typename, ...filteredContact } = contact;
                    return filteredContact;
                  });
                }

                if (filteredExistingOrSimilar.relatedAddressList) {
                  filteredExistingOrSimilar.relatedAddressList = filteredExistingOrSimilar.relatedAddressList.map((address: any) => {
                    const { __typename, ...filteredAddress } = address;
                    return filteredAddress;
                  });
                }

                return filteredExistingOrSimilar;
              })() : undefined
            }
          }
        });

        if (confirmResponse.data?.saveCooperationWarehouse) {
          const confirmResult = confirmResponse.data.saveCooperationWarehouse.result;
          if (confirmResult === 'success') {
            message.success(mode === 'create' ? 'Pickup Site created successfully!' : 'Pickup Site updated successfully!');
            onSubmit(confirmResponse.data);
          } else {
            message.error('Operation failed. Please try again.');
          }
        }
      } catch (error: any) {
        console.error('Confirm submit failed:', error);
        message.error(error.message || 'Operation failed. Please try again.');
      } finally {
        setIsModalConfirming(false);
      }
    }

    // 关闭弹窗
    setModalState({
      visible: false,
      type: null,
      data: null
    });
  };

  const handleModalCancel = () => {
    // 关闭弹窗
    setModalState({
      visible: false,
      type: null,
      data: null
    });
    setIsModalConfirming(false);
  };

  return (
    <>
      <style>{drawerStyles}</style>
      <EditableDrawer
        key={mode}
        title={drawerTitle}
        visible={visible}
        onClose={onClose}
        onSubmit={mode === 'view' ? handleViewSubmit : handleSubmit}
        confirmText={mode === 'view' ? t('pickup.cooperationWarehouse.buttons.edit') as string : (isSubmitting ? t('pickup.cooperationWarehouse.buttons.processing') as string : t('pickup.cooperationWarehouse.buttons.confirm') as string)}
        cancelText={t('pickup.cooperationWarehouse.buttons.cancel') as string}
        confirmDisabled={isSubmitting || isLoading}
        width={1000}
        className="cooperation-warehouse-drawer"
      >
        {mode === 'view' ? (
          <Skeleton loading={isLoading} active>
            <div className="space-y-4">
              {/* Basic Information */}
              <div className="flex items-center">
                <label className="text-sm font-medium text-gray-500 mr-2 text-left" style={viewLabelStyle}>{t('pickup.cooperationWarehouse.pageDetails.id') as string}:</label>
                <span className="text-gray-900">{initialValues?.id}</span>
              </div>
              <div className="flex items-start">
                <label className="text-sm font-medium text-gray-500 mr-2 text-left" style={viewLabelStyle}>{t('pickup.cooperationWarehouse.pageDetails.name') as string}:</label>
                <span className="text-gray-900">{initialValues?.name}</span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center">
                  <label className="text-sm font-medium text-gray-500 mr-2 text-left" style={viewLabelStyle}>{t('pickup.cooperationWarehouse.pageDetails.hub') as string}:</label>
                  <span className="text-gray-900">{initialValues?.warehouseName}</span>
                </div>
                <div className="flex items-center">
                  <label className="text-sm font-medium text-gray-500 mr-2 text-left" style={viewLabelStyle}>{t('pickup.cooperationWarehouse.pageDetails.status') as string}:</label>
                  <span className="text-gray-900">{getStatusText(initialValues?.status)}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center">
                  <label className="text-sm font-medium text-gray-500 mr-2 text-left" style={viewLabelStyle}>{t('pickup.cooperationWarehouse.filters.standingPickup') as string}:</label>
                  <span className="text-gray-900">{initialValues?.standingPickup === false ? 'NO' : 'YES'}</span>
                </div>
                {initialValues?.standingPickup === true && (
                  <div className="flex items-center">
                    <label className="text-sm font-medium text-gray-500 mr-2 text-left"
                      style={viewLabelStyle}><span>{t('pickup.cooperationWarehouse.filters.standingPickup')} <br />{t('pickup.cooperationWarehouse.filters.appointmentCustomer')}</span>:</label>
                    <span className="text-gray-900">{getChannelText(initialValues?.channelId)}</span>
                  </div>
                )}
              </div>

              <div className="flex items-start">
                <label className="text-sm font-medium text-gray-500 mr-2 text-left" style={viewLabelStyle}>{t('pickup.cooperationWarehouse.pageDetails.relatedChannels') as string}:</label>
                <span className="text-gray-900 flex-1">{getChannelTexts(initialValues?.relatedChannelIds || [])}</span>
              </div>

              {/* Address */}
              <div className="flex items-start">
                <label className="text-sm font-medium text-gray-500 mr-2 text-left" style={viewLabelStyle}>{t('pickup.cooperationWarehouse.pageDetails.address') as string}:</label>
                <span className="text-gray-900 flex-1" style={{ whiteSpace: 'pre-wrap' }}>{formatAddress(initialValues)}</span>
              </div>

              {/* Contact Information */}
              <div className="flex items-start">
                <label className="text-sm font-medium text-gray-500 mr-2 text-left" style={viewLabelStyle}>{t('pickup.cooperationWarehouse.pageDetails.contactPerson') as string}:</label>
                <div className="text-gray-900 flex-1">
                  {initialValues?.contactPersonList && initialValues.contactPersonList.length > 0 ? (
                    initialValues.contactPersonList.map((contact: any, index: number) => {
                      const hasName = contact.name && contact.name.trim() !== '';
                      const hasPhone = contact.phone && contact.phone.trim() !== '';
                      if (hasName || hasPhone) {
                        const countryCode = contact.countryCode || '';
                        const extension = contact.extension ? `-${contact.extension}` : '';
                        const phoneDisplay = hasPhone ? `+${countryCode} ${contact.phone}${extension}` : '';
                        return (
                          <div key={index} style={{ marginBottom: index < initialValues.contactPersonList.length - 1 ? '4px' : '0' }}>
                            {hasName ? contact.name : '-'} {hasPhone ? `(${phoneDisplay})` : ''} {contact.inBlacklist ? `（${t('pickup.cooperationWarehouse.pageDetails.inBlacklist')}）` : ''}
                          </div>
                        );
                      }
                      return null;
                    }).filter(Boolean)
                  ) : (
                    <span>-</span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center">
                  <label className="text-sm font-medium text-gray-500 mr-2 text-left" style={viewLabelStyle}>{t('pickup.cooperationWarehouse.pageDetails.longitude') as string}:</label>
                  <span className="text-gray-900">{initialValues?.longitude}</span>
                </div>
                <div className="flex items-center">
                  <label className="text-sm font-medium text-gray-500 mr-2 text-left" style={viewLabelStyle}>{t('pickup.cooperationWarehouse.pageDetails.latitude') as string}:</label>
                  <span className="text-gray-900">{initialValues?.latitude}</span>
                </div>
              </div>

              <div className="flex items-center">
                <label className="text-sm font-medium text-gray-500 mr-2 text-left" style={viewLabelStyle}>{t('pickup.cooperationWarehouse.pageDetails.email') as string}:</label>
                <span className="text-gray-900">{initialValues?.email}</span>
              </div>

              <div className="flex items-center">
                <label className="text-sm font-medium text-gray-500 mr-2 text-left" style={viewLabelStyle}>{t('pickup.cooperationWarehouse.pageDetails.businessHours') as string}:</label>
                <span className="text-gray-900">{formatWorkTime(initialValues)}</span>
              </div>

              {initialValues?.standingPickup === true && (
                <div className="flex items-center">
                  <label className="text-sm font-medium text-gray-500 mr-2 text-left" style={viewLabelStyle}>{t('pickup.cooperationWarehouse.pageDetails.expectedPickupTime') as string}:</label>
                  <span className="text-gray-900">
                    {initialValues?.expectedPickupStartTime && initialValues?.expectedPickupEndTime
                      ? `${initialValues.expectedPickupStartTime} - ${initialValues.expectedPickupEndTime}`
                      : '-'}
                  </span>
                </div>
              )}

              <div className="flex items-center">
                <label className="text-sm font-medium text-gray-500 mr-2 text-left" style={viewLabelStyle}>{t('pickup.cooperationWarehouse.pageDetails.useVehicleModel') as string}:</label>
                <span className="text-gray-900">
                  {(() => {
                    if (!initialValues?.vehicleTypeId) return '-';
                    const vehicleModel = vehicleModelOptions.find(opt => opt.value === initialValues.vehicleTypeId);
                    return vehicleModel?.label || '-';
                  })()}
                </span>
              </div>

              <div className="flex items-start">
                <label className="text-sm font-medium text-gray-500 mr-2 text-left" style={viewLabelStyle}>{t('pickup.cooperationWarehouse.pageDetails.alias') as string}:</label>
                <span className="text-gray-900 flex-1">{formatAliases(initialValues?.aliasList)}</span>
              </div>

              <div className="flex items-start">
                <label className="text-sm font-medium text-gray-500 mr-2 text-left" style={viewLabelStyle}>{t('pickup.cooperationWarehouse.pageDetails.relatedAddress') as string}:</label>
                <div className="text-gray-900 flex-1">
                  {(() => {
                    const addresses = formatRelatedAddresses(initialValues?.relatedAddressList);
                    if (!addresses || addresses === '-') return '-';

                    const lines = addresses.split('\n');
                    if (lines.length === 1) {
                      return <span>{lines[0]}</span>;
                    } else {
                      return (
                        <div>
                          <span>{lines[0]}</span>
                          {lines.slice(1).map((line, index) => (
                            <div key={index} className="mt-1 ml-0" style={{ marginLeft: '0' }}>
                              {line}
                            </div>
                          ))}
                        </div>
                      );
                    }
                  })()}
                </div>
              </div>

              <div className="flex items-center">
                <label className="text-sm font-medium text-gray-500 mr-2 text-left" style={viewLabelStyle}>{t('pickup.cooperationWarehouse.pageDetails.remark') as string}:</label>
                <span className="text-gray-900">{initialValues?.remark}</span>
              </div>
            </div>
          </Skeleton>
        ) : (
          // 编辑模式 - 保持原来的表单结构
          <Form
            form={form}
            layout="horizontal"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
            initialValues={{
              ...initialValues,
              workStartTime: initialValues?.workStartTime ? (dayjs.isDayjs(initialValues.workStartTime) ? initialValues.workStartTime : dayjs(initialValues.workStartTime, 'HH:mm')) : dayjs().hour(9).minute(0).second(0),
              workEndTime: initialValues?.workEndTime ? (dayjs.isDayjs(initialValues.workEndTime) ? initialValues.workEndTime : dayjs(initialValues.workEndTime, 'HH:mm')) : dayjs().hour(18).minute(0).second(0),
              expectedPickupStartTime: initialValues?.expectedPickupStartTime ? (dayjs.isDayjs(initialValues.expectedPickupStartTime) ? initialValues.expectedPickupStartTime : dayjs(initialValues.expectedPickupStartTime, 'HH:mm')) : dayjs().hour(9).minute(0).second(0),
              expectedPickupEndTime: initialValues?.expectedPickupEndTime ? (dayjs.isDayjs(initialValues.expectedPickupEndTime) ? initialValues.expectedPickupEndTime : dayjs(initialValues.expectedPickupEndTime, 'HH:mm')) : dayjs().hour(18).minute(0).second(0),
              vehicleTypeId: initialValues?.vehicleTypeId,
            }}
            labelAlign="left"
            colon={true}
          >
            {/* Basic Information Row */}
            {(mode === 'edit') && (
              <Row>
                <Col span={12}>
                  <Form.Item label={t('pickup.cooperationWarehouse.pageDetails.id') as string} labelCol={{ span: 12, style: labelStyle }} wrapperCol={{ span: 12 }}>
                    <span>
                      {initialValues?.id || ''}
                    </span>
                  </Form.Item>
                </Col>
              </Row>
            )}
            <Row>
              <Col span={24}>
                <Form.Item
                  name="name"
                  label={t('pickup.cooperationWarehouse.pageDetails.name') as string}
                  rules={[{ required: true, message: t('pickup.cooperationWarehouse.messages.pleaseEnterWarehouseName') as string }]}
                >
                  <Input placeholder={t('pickup.cooperationWarehouse.placeholders.warehouseName') as string} style={inputStyle} />
                </Form.Item>
              </Col>
            </Row>

            <Row>
              <Col span={12}>
                <Form.Item
                  name="warehouseId"
                  label={t('pickup.cooperationWarehouse.pageDetails.hub') as string}
                  rules={[{ required: true, message: t('pickup.cooperationWarehouse.messages.pleaseSelectHub') as string }]}
                  labelCol={{ span: 12, style: labelStyle }} wrapperCol={{ span: 12 }}
                >
                  <Select
                    options={hubOptions}
                    disabled={true}
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="status"
                  label={t('pickup.cooperationWarehouse.pageDetails.status') as string}
                  rules={[{ required: true, message: t('pickup.cooperationWarehouse.messages.pleaseSelectStatus') as string }]}
                  labelCol={{ span: 12, style: labelStyle }} wrapperCol={{ span: 15 }}
                >
                  <Select placeholder={t('pickup.cooperationWarehouse.placeholders.selectStatus') as string} style={selectStyle}>
                    {(() => {
                      // 根据模式和数据状态动态过滤选项
                      let filteredOptions = statusOptions;

                      if (mode === 'create') {
                        // 新增模式：不包含待审核状态（通常 value = 2）
                        filteredOptions = statusOptions.filter(option => option.value !== 2);
                        filteredOptions = filteredOptions.filter(option => option.value !== 0);//暂时不提供修改状态为INACTIVE
                      } else if (mode === 'edit' && initialValues) {
                        // 编辑模式：如果当前数据是待审核状态，则包含待审核选项，否则不包含
                        const currentStatus = initialValues.status;
                        if (currentStatus === 2) {
                          // 当前是待审核状态，包含所有选项
                          filteredOptions = statusOptions;
                        } else {
                          // 当前不是待审核状态，不包含待审核选项
                          filteredOptions = statusOptions.filter(option => option.value !== 2);
                        }
                        if (currentStatus !== 0) {
                          // 当前不是停用状态，不包含停用选项
                          filteredOptions = filteredOptions.filter(option => option.value !== 0);//暂时不提供修改状态为INACTIVE
                        }
                      }

                      return filteredOptions.map(option => (
                        <Select.Option key={option.value} value={option.value}>
                          {option.label}
                        </Select.Option>
                      ));
                    })()}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row>
              <Col span={12}>
                <Form.Item
                  name="standingPickup"
                  label={t('pickup.cooperationWarehouse.filters.standingPickup') as string}
                  rules={[{ required: true, message: t('pickup.cooperationWarehouse.messages.pleaseSelectStandingPickup') as string }]}
                  labelCol={{ span: 12, style: labelStyle }} wrapperCol={{ span: 12 }}
                >
                  <Select
                    placeholder={t('pickup.cooperationWarehouse.placeholders.selectStandingPickup') as string}
                    style={selectStyle}
                    onChange={(value) => {
                      setStandingPickupValue(value);
                      // 如果选择 NO，清空 channelId 字段
                      if (value === false) {
                        form.setFieldValue('channelId', undefined);
                      }
                    }}
                  >
                    {(() => {
                      // 根据模式和数据状态动态过滤选项
                      let filteredOptions = [
                        { label: t('enum.yesorNoStatus.yes'), value: true },
                        { label: t('enum.yesorNoStatus.no'), value: false }
                      ];
                      return filteredOptions.map(option => (
                        <Select.Option key={String(option.value)} value={option.value}>
                          {option.label}
                        </Select.Option>
                      ));
                    })()}
                  </Select>
                </Form.Item>
              </Col>
              {standingPickupValue === true && (
                <Col span={12}>
                  <Form.Item
                    name="channelId"
                    label={<span style={labelStyleCustomer}>{t('pickup.cooperationWarehouse.filters.standingPickup')} <br />{t('pickup.cooperationWarehouse.filters.appointmentCustomer')}</span>}
                    rules={[{ required: true, message: t('pickup.cooperationWarehouse.messages.pleaseSelectStandingPickupAppointmentCustomer') as string }]}
                    labelCol={{ span: 12, style: labelStyleCustomer }} wrapperCol={{ span: 12 }}
                  >
                    <Select
                      placeholder={t('pickup.cooperationWarehouse.placeholders.selectCustomer') as string}
                      style={selectStyle}
                      value={initialValues?.channelId}
                    >
                      {(() => {
                        return channelOptions.map(option => (
                          <Select.Option key={option.value} value={option.value}>
                            {option.label}
                          </Select.Option>
                        ));
                      })()}
                    </Select>
                  </Form.Item>
                </Col>
              )}
            </Row>

            <Row>
              <Col span={12}>
                <Form.Item
                  name="relatedChannelIds"
                  label={t('pickup.cooperationWarehouse.pageDetails.relatedChannels') as string}
                  labelCol={{ span: 12, style: labelStyle }} wrapperCol={{ span: 12 }}
                >
                  <Select
                    mode="multiple"
                    placeholder={t('pickup.cooperationWarehouse.placeholders.selectRelatedChannels') as string}
                    style={selectStyle}
                    loading={channelLoading}
                  >
                    {channelOptions.map(option => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* Cooperation Address Section */}
            <Form.Item
              label={
                <span className="required-field-label">
                  {t('pickup.cooperationWarehouse.pageDetails.address') as string}
                </span>
              }
              labelCol={{ span: 6, style: labelStyle }} wrapperCol={{ span: 18 }}>
              <Form.Item
                name="addressLine1"
                style={{ marginBottom: 12 }}
                rules={[{ required: true, message: t('pickup.cooperationWarehouse.messages.pleaseEnterTheDetailedAddress') as string }]}
              >
                <Input placeholder={t('pickup.cooperationWarehouse.placeholders.detailedAddress1') as string} style={inputStyle} />
              </Form.Item>
              <Form.Item name="addressLine2" style={{ marginBottom: 12 }}>
                <Input placeholder={t('pickup.cooperationWarehouse.placeholders.detailedAddress2') as string} style={inputStyle} />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0 }}>
                <CascadingAddressSelector
                  countryFieldName="country"
                  stateFieldName="state"
                  cityFieldName="city"
                  zipcodeFieldName="zipcode"
                  countryPlaceholder={t('pickup.cooperationWarehouse.placeholders.country') as string}
                  statePlaceholder={t('pickup.cooperationWarehouse.placeholders.state') as string}
                  cityPlaceholder={t('pickup.cooperationWarehouse.placeholders.city') as string}
                  zipcodePlaceholder={t('pickup.cooperationWarehouse.placeholders.zipcode') as string}
                  initialCountry={t('pickup.cooperationWarehouse.placeholders.us') as string}
                  initialState={initialValues?.state || undefined}
                  initialCity={initialValues?.city || undefined}
                  initialZipcode={initialValues?.zipcode || undefined}
                  countryWidth="20%"
                  stateWidth="20%"
                  cityWidth="35%"
                  zipcodeWidth="25%"
                  form={form}
                />
              </Form.Item>
            </Form.Item>

            {/* Contact Person and Phone Section */}
            <Form.Item
              label={
                <span className="required-field-label">
                  {t('pickup.cooperationWarehouse.pageDetails.contactPerson') as string} & {t('pickup.cooperationWarehouse.pageDetails.phone') as string}
                </span>
              }
              labelCol={{ span: 6, style: labelStyle }}
              wrapperCol={{ span: 18 }}
            >
              <Form.List name="contactPersonList" initialValue={[{ name: '', phone: '', countryCode: 1, extension: '', inBlacklist: false }]}>
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }, index) => (
                      <Row gutter={16} key={key} style={{ marginBottom: 8 }}>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, 'name']}
                            style={{ marginBottom: 0 }}
                            rules={[
                              {
                                validator: (_, value) => {
                                  // 第一行必填
                                  if (index === 0) {
                                    if (!value || value.trim() === '') {
                                      return Promise.reject(new Error(t('pickup.cooperationWarehouse.messages.pleaseEnterContactPerson') as string));
                                    }
                                  } else {
                                    // 其他行：如果填写了姓名，则电话也必须填写
                                    const phoneValue = form.getFieldValue(['contactPersonList', name, 'phone']);
                                    if (value && value.trim() !== '' && (!phoneValue || phoneValue.trim() === '')) {
                                      return Promise.reject(new Error(t('pickup.cooperationWarehouse.messages.phoneNumberRequired') as string));
                                    }
                                  }
                                  return Promise.resolve();
                                }
                              }
                            ]}
                          >
                            <Input
                              placeholder={t('pickup.cooperationWarehouse.placeholders.contactPerson') as string}
                              style={inputStyle}
                              onChange={(e) => {
                                // 当姓名变化时，触发电话字段的校验
                                if (index > 0) {
                                  setTimeout(() => {
                                    form.validateFields([['contactPersonList', name, 'phone']]);
                                  }, 0);
                                }
                              }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={10}>
                          <Space.Compact block>
                            <Form.Item
                              {...restField}
                              name={[name, 'countryCode']}
                              noStyle
                              initialValue={1}
                            >
                              <Select style={{ width: 70 }} placeholder="Code">
                                <Select.Option value={1}>+1</Select.Option>
                                <Select.Option value={86}>+86</Select.Option>
                              </Select>
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              name={[name, 'phone']}
                              noStyle
                              rules={[
                                {
                                  validator: (_, value) => {
                                    // 第一行必填
                                    if (index === 0) {
                                      if (!value || value.trim() === '') {
                                        return Promise.reject(new Error(t('pickup.cooperationWarehouse.messages.pleaseEnterPhoneNumber') as string));
                                      }
                                      // 校验电话号码格式
                                      const phoneRegex = /^\d{7,12}$/;
                                      if (!phoneRegex.test(value.trim())) {
                                        return Promise.reject(new Error(t('pickup.cooperationWarehouse.messages.phoneNumberLength') as string));
                                      }
                                    } else {
                                      // 其他行：如果填写了电话，则姓名也必须填写
                                      const nameValue = form.getFieldValue(['contactPersonList', name, 'name']);
                                      if (value && value.trim() !== '') {
                                        if (!nameValue || nameValue.trim() === '') {
                                          return Promise.reject(new Error(t('pickup.cooperationWarehouse.messages.pleaseEnterContactPerson') as string));
                                        }
                                        // 校验电话号码格式
                                        const phoneRegex = /^\d{7,12}$/;
                                        if (!phoneRegex.test(value.trim())) {
                                          return Promise.reject(new Error(t('pickup.cooperationWarehouse.messages.phoneNumberLength') as string));
                                        }
                                      }
                                    }
                                    return Promise.resolve();
                                  }
                                }
                              ]}
                            >
                              <Input
                                style={{ flex: 1 }}
                                placeholder={t('pickup.cooperationWarehouse.placeholders.phoneNumber') as string}
                                onChange={(e) => {
                                  // 当电话变化时，触发姓名字段的校验
                                  if (index > 0) {
                                    setTimeout(() => {
                                      form.validateFields([['contactPersonList', name, 'name']]);
                                    }, 0);
                                  }
                                }}
                              />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              name={[name, 'extension']}
                              noStyle
                              rules={[
                                {
                                  pattern: /^\d*$/,
                                  message: 'Only numbers are allowed',
                                },
                                {
                                  validator: (_, value) => {
                                    if (value && value.length > 10) {
                                      return Promise.reject(new Error(t('delivery.parcels.label.extensionTooLong') as string));
                                    }
                                    return Promise.resolve();
                                  }
                                },
                              ]}
                            >
                              <Input style={{ width: 80, marginLeft: '1px', borderLeftColor: 'transparent' }} placeholder={String(t('delivery.parcels.label.ext'))} />
                            </Form.Item>
                          </Space.Compact>
                        </Col>
                        <Col span={4} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
                          <span style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
                            {t('pickup.cooperationWarehouse.pageDetails.blacklist') as string}
                          </span>
                          <Tooltip title={t('pickup.cooperationWarehouse.pageDetails.blacklistTooltip') as string}>
                            <InfoCircleOutlined style={{ marginLeft: 0, color: '#1890ff', cursor: 'help' }} />
                          </Tooltip>
                          <Form.Item
                            {...restField}
                            name={[name, 'inBlacklist']}
                            style={{ marginBottom: 0 }}
                            valuePropName="checked"
                            initialValue={false}
                          >
                            <Switch
                              onChange={(checked) => {
                                // 获取当前联系人的手机号
                                const currentPhone = form.getFieldValue(['contactPersonList', name, 'phone']);

                                if (!currentPhone || currentPhone.trim() === '') {
                                  // 如果没有手机号，只更新当前项
                                  form.setFieldValue(['contactPersonList', name, 'inBlacklist'], checked);
                                  return;
                                }

                                if (checked) {
                                  // 先阻止开关打开，将值重置为 false
                                  form.setFieldValue(['contactPersonList', name, 'inBlacklist'], false);

                                  // 显示确认对话框
                                  Modal.confirm({
                                    title: t('pickup.cooperationWarehouse.pageDetails.blacklistTooltip') as string,
                                    okText: t('components.button.confirm') as string,
                                    cancelText: t('components.button.cancel') as string,
                                    onOk: () => {
                                      // 用户确认，同步更新所有相同手机号的联系人的 inBlacklist
                                      const contactPersonList = form.getFieldValue('contactPersonList') || [];
                                      const phoneToMatch = currentPhone.trim();

                                      contactPersonList.forEach((_: any, index: number) => {
                                        const phone = form.getFieldValue(['contactPersonList', index, 'phone']);
                                        if (phone && phone.trim() === phoneToMatch) {
                                          form.setFieldValue(['contactPersonList', index, 'inBlacklist'], true);
                                        }
                                      });
                                    },
                                    onCancel: () => {
                                      // 用户取消，保持开关关闭（值已经是 false）
                                    }
                                  });
                                } else {
                                  // 开关关闭时，同步关闭所有相同手机号的联系人的 inBlacklist
                                  const contactPersonList = form.getFieldValue('contactPersonList') || [];
                                  const phoneToMatch = currentPhone.trim();

                                  contactPersonList.forEach((_: any, index: number) => {
                                    const phone = form.getFieldValue(['contactPersonList', index, 'phone']);
                                    if (phone && phone.trim() === phoneToMatch) {
                                      form.setFieldValue(['contactPersonList', index, 'inBlacklist'], false);
                                    }
                                  });
                                }
                              }}
                            />
                          </Form.Item>
                        </Col>
                        {index === 0 && (
                          <Col span={2}>
                            <Button
                              type="link"
                              icon={<PlusOutlined />}
                              onClick={() => {
                                // 添加新行前的校验
                                const currentName = form.getFieldValue(['contactPersonList', name, 'name']);
                                const currentPhone = form.getFieldValue(['contactPersonList', name, 'phone']);

                                if (index === 0) {
                                  // 第一行必须完整填写
                                  if (!currentName || currentName.trim() === '' || !currentPhone || currentPhone.trim() === '') {
                                    message.error(t('pickup.cooperationWarehouse.messages.pleaseFillInBothContactPersonAndPhoneNumberInTheFirstRowBeforeAddingANewOne') as string);
                                    return;
                                  }
                                  // 校验电话号码格式
                                  const phoneRegex = /^\d{7,12}$/;
                                  if (!phoneRegex.test(currentPhone.trim())) {
                                    message.error(t('pickup.cooperationWarehouse.messages.phoneNumberLengthInTheFirstRow') as string);
                                    return;
                                  }
                                } else {
                                  // 其他行如果填写了任一字段，则必须都填写
                                  if ((currentName && currentName.trim() !== '') || (currentPhone && currentPhone.trim() !== '')) {
                                    if (!currentName || currentName.trim() === '' || !currentPhone || currentPhone.trim() === '') {
                                      message.error(t('pickup.cooperationWarehouse.messages.pleaseFillInBothContactPersonAndPhoneNumberInTheCurrentRowBeforeAddingANewOne') as string);
                                      return;
                                    }
                                    // 校验电话号码格式
                                    const phoneRegex = /^\d{7,12}$/;
                                    if (!phoneRegex.test(currentPhone.trim())) {
                                      message.error(t('pickup.cooperationWarehouse.messages.phoneNumberLengthInTheCurrentRow') as string);
                                      return;
                                    }
                                  }
                                }
                                add();
                              }}
                            />
                          </Col>
                        )}
                        {index > 0 && (
                          <Col span={2}>
                            <Button
                              type="text"
                              icon={<MinusOutlined />}
                              danger
                              onClick={() => remove(name)}
                            />
                          </Col>
                        )}
                      </Row>
                    ))}
                  </>
                )}
              </Form.List>
            </Form.Item>

            <Form.Item
              name="workWeekdayList"
              label={t('pickup.cooperationWarehouse.pageDetails.workDays') as string}
              rules={[{ required: true, message: t('pickup.cooperationWarehouse.messages.pleaseSelectWorkDays') as string }]}
              initialValue={[1, 2, 3, 4, 5]} // 默认周一到周五
              labelCol={{ span: 6, style: labelStyle }} wrapperCol={{ span: 18 }}
            >
              <Select
                mode="multiple"
                placeholder={t('pickup.cooperationWarehouse.placeholders.selectWorkDays') as string}
                style={{ width: '100%' }}
              >
                {getWeekdaysOptions(t).map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={
                <span className="required-field-label">
                  {t('pickup.cooperationWarehouse.pageDetails.workTime') as string}
                </span>
              }
              labelCol={{ span: 6, style: labelStyle }} wrapperCol={{ span: 18 }}
            >
              <Space.Compact block>
                <Form.Item
                  name="workStartTime"
                  noStyle
                  rules={[{ required: true, message: t('pickup.cooperationWarehouse.messages.pleaseSelectStartTime') as string }]}
                >
                  <TimePicker
                    style={{ width: '30%' }}
                    placeholder={t('pickup.cooperationWarehouse.placeholders.startTime') as string}
                    format="HH:mm"
                  />
                </Form.Item>
                <Input
                  style={{ width: '10%', textAlign: 'center' }}
                  placeholder="~"
                  disabled
                  className="bg-gray-50"
                />
                <Form.Item
                  name="workEndTime"
                  noStyle
                  rules={[
                    { required: true, message: t('pickup.cooperationWarehouse.messages.pleaseSelectEndTime') as string },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const startTime = getFieldValue('workStartTime');
                        if (!value || !startTime) {
                          return Promise.resolve();
                        }
                        if (value.isAfter(startTime)) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Work end time must be after start time'));
                      },
                    }),
                  ]}
                >
                  <TimePicker style={{ width: '30%' }} format="HH:mm" placeholder={t('pickup.cooperationWarehouse.placeholders.endTime') as string} />
                </Form.Item>
              </Space.Compact>
            </Form.Item>

            {standingPickupValue === true && (
              <Form.Item
                label={
                  <span className="required-field-label">
                    {t('pickup.cooperationWarehouse.pageDetails.expectedPickupTime') as string}
                  </span>
                }
                labelCol={{ span: 6, style: labelStyle }} wrapperCol={{ span: 18 }}
              >
                <Space.Compact block>
                  <Form.Item
                    name="expectedPickupStartTime"
                    noStyle
                    rules={[{ required: true, message: t('pickup.cooperationWarehouse.messages.pleaseSelectStartTime') as string }]}
                  >
                    <TimePicker
                      style={{ width: '30%' }}
                      placeholder={t('pickup.cooperationWarehouse.placeholders.startTime') as string}
                      format="HH:mm"
                    />
                  </Form.Item>
                  <Input
                    style={{ width: '10%', textAlign: 'center' }}
                    placeholder="~"
                    disabled
                    className="bg-gray-50"
                  />
                  <Form.Item
                    name="expectedPickupEndTime"
                    noStyle
                    rules={[
                      { required: true, message: t('pickup.cooperationWarehouse.messages.pleaseSelectEndTime') as string },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const startTime = getFieldValue('expectedPickupStartTime');
                          if (!value || !startTime) {
                            return Promise.resolve();
                          }
                          if (value.isAfter(startTime)) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('Expected pickup end time must be after start time'));
                        },
                      }),
                    ]}
                  >
                    <TimePicker style={{ width: '30%' }} format="HH:mm" placeholder={t('pickup.cooperationWarehouse.placeholders.endTime') as string} />
                  </Form.Item>
                </Space.Compact>
              </Form.Item>
            )}

            <Row>
              <Col span={12}>
                <Form.Item
                  name="vehicleTypeId"
                  label={t('pickup.cooperationWarehouse.pageDetails.useVehicleModel') as string}
                  rules={[{ required: true, message: t('pickup.cooperationWarehouse.messages.pleaseSelectVehicleModel') as string }]}
                  labelCol={{ span: 12, style: labelStyle }} wrapperCol={{ span: 12 }}
                >
                  <Select
                    placeholder={t('pickup.cooperationWarehouse.placeholders.selectVehicleModel') as string}
                    style={selectStyle}
                    loading={vehicleLoading}
                  >
                    {vehicleModelActiveOptions.map(option => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* Contact Info Row */}
            <Row>
              <Col span={12}>
                <Form.Item name="longitude" label={t('pickup.cooperationWarehouse.pageDetails.longitude') as string} labelCol={{ span: 12, style: labelStyle }} wrapperCol={{ span: 12 }}>
                  <InputNumber
                    placeholder={t('pickup.cooperationWarehouse.placeholders.longitude') as string}
                    style={inputStyle}
                    precision={7}
                    min={-180}
                    max={180}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="latitude" label={t('pickup.cooperationWarehouse.pageDetails.latitude') as string} labelCol={{ span: 6, style: labelStyle }} wrapperCol={{ span: 12 }}>
                  <InputNumber
                    placeholder={t('pickup.cooperationWarehouse.placeholders.latitude') as string}
                    style={inputStyle}
                    precision={7}
                    min={-90}
                    max={90}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="email"
              label={t('pickup.cooperationWarehouse.pageDetails.email') as string}
              rules={[{ type: 'email', message: 'Please enter valid email' }]}
              labelCol={{ span: 6, style: labelStyle }} wrapperCol={{ span: 18 }}
            >
              <Input placeholder={t('pickup.cooperationWarehouse.placeholders.email') as string} style={inputStyle} />
            </Form.Item>


            {/* Cooperation List Section */}
            <Form.Item label={t('pickup.cooperationWarehouse.pageDetails.alias') as string} labelCol={{ span: 6, style: labelStyle }} wrapperCol={{ span: 18 }}>
              {aliasList.map((alias, index) => (
                <Row gutter={16} key={index} style={{ marginBottom: 8 }}>
                  <Col span={22}>
                    <Input
                      placeholder={t('pickup.cooperationWarehouse.placeholders.alias') as string}
                      style={inputStyle}
                      value={alias}
                      onChange={(e) => handleAliasChange(index, e.target.value)}
                    />
                  </Col>
                  {index === 0 && (
                    <Col span={2}>
                      <Button
                        type="link"
                        icon={<PlusOutlined />}
                        onClick={handleAddAlias}
                      >
                      </Button>
                    </Col>
                  )}
                  {index > 0 && (
                    <Col span={2}>
                      <Button
                        type="text"
                        icon={<MinusOutlined />}
                        danger
                        onClick={() => handleRemoveAlias(index)}
                      />
                    </Col>
                  )}
                </Row>
              ))}
            </Form.Item>

            {/* Related Addresses Section */}
            <Form.Item label={t('pickup.cooperationWarehouse.pageDetails.relatedAddress') as string} labelCol={{ span: 6, style: labelStyle }} wrapperCol={{ span: 18 }}>
              {relatedAddressList.map((address, index) => (
                <Row gutter={16} key={index} style={{ marginBottom: 8 }}>
                  <Col span={11}>
                    <Input
                      placeholder={t('pickup.cooperationWarehouse.placeholders.detailedAddress1') as string}
                      style={inputStyle}
                      value={address.address1}
                      onChange={(e) => handleAddressChange(index, 'address1', e.target.value)}
                    />
                  </Col>
                  <Col span={11}>
                    <Input
                      placeholder={t('pickup.cooperationWarehouse.placeholders.detailedAddress2') as string}
                      style={inputStyle}
                      value={address.address2}
                      onChange={(e) => handleAddressChange(index, 'address2', e.target.value)}
                    />
                  </Col>
                  {index === 0 && (
                    <Col span={2}>
                      <Button
                        type="link"
                        icon={<PlusOutlined />}
                        onClick={handleAddAddress}
                      >
                      </Button>
                    </Col>
                  )}
                  {index > 0 && (
                    <Col span={2}>
                      <Button
                        type="text"
                        icon={<MinusOutlined />}
                        danger
                        onClick={() => handleRemoveAddress(index)}
                      />
                    </Col>
                  )}
                </Row>
              ))}
            </Form.Item>
            <Form.Item
              name="remark"
              label={t('pickup.cooperationWarehouse.pageDetails.remark') as string}
              rules={[
                {
                  max: 200,
                  message: 'Remark cannot exceed 200 characters'
                }
              ]}
              labelCol={{ span: 6, style: labelStyle }}
              wrapperCol={{ span: 18 }}
            >
              <Input.TextArea
                placeholder="remark"
                style={inputStyle}
                rows={4}
                showCount
                maxLength={200}
              />
            </Form.Item>

          </Form>
        )}
      </EditableDrawer>

      {/* 弹窗组件 */}
      {modalState.type === 'same' && (
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <ExclamationCircleOutlined style={{ marginRight: 8, color: '#faad14' }} />
              Repetitive storage rooms are not allowed to be added.
            </div>
          }
          open={modalState.visible}
          onOk={handleModalOk}
          okText="OK"
          width={600}
          style={{
            maxHeight: '80vh'
          }}
          centered
          closable={false}
          className="custom-modal"
          footer={[
            <Button key="ok" type="primary" onClick={handleModalOk}>
              OK
            </Button>
          ]}
        >
          <div style={{ minHeight: '120px', padding: '20px 0' }}>
            <p>The system has detected that the name and address of this warehouse already exist and cannot be added again.</p>
            <p style={{ marginTop: 8, fontWeight: 'bold', textAlign: 'center' }}>
              {modalState.data?.existingOrSimilar?.name}: {formatAddress(modalState.data?.existingOrSimilar)}
            </p>
            <p style={{ marginTop: 4, fontWeight: 'bold', textAlign: 'center' }}>
              (Contact Person: {formatContactPerson(modalState.data?.existingOrSimilar?.contactPersonList || [])}, phone: {formatPhoneNumber(modalState.data?.existingOrSimilar?.contactPersonList || [])})
            </p>
            {modalState.data?.existingOrSimilar?.status === 0 && (
              <p style={{ marginTop: 4, fontWeight: 'bold', textAlign: 'center', color: '#ff4d4f' }}>
                INACTIVE ADDRESS!!!
              </p>
            )}
          </div>
        </Modal>
      )}

      {modalState.type === 'similar' && (
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <ExclamationCircleOutlined style={{ marginRight: 8, color: '#faad14' }} />
              Found duplicate address records
            </div>
          }
          open={modalState.visible}
          onOk={handleModalOk}
          onCancel={handleModalCancel}
          okText={isModalConfirming ? "Processing..." : "Confirm"}
          cancelText="Cancel"
          okButtonProps={{ disabled: isModalConfirming }}
          width={600}
          style={{
            maxHeight: '80vh'
          }}
          centered
          closable={false}
          className="custom-modal"
        >
          <div style={{ minHeight: '120px', padding: '20px 0' }}>
            <p>The system has detected that there is a similar cooperative warehouse address matching the information you entered:</p>
            <p style={{ marginTop: 8, fontWeight: 'bold', textAlign: 'center' }}>
              {modalState.data?.existingOrSimilar?.name}: {formatAddress(modalState.data?.existingOrSimilar)}
            </p>
            <p style={{ marginTop: 4, fontWeight: 'bold', textAlign: 'center' }}>
              (Contact Person: {formatContactPerson(modalState.data?.existingOrSimilar?.contactPersonList || [])}, phone: {formatPhoneNumber(modalState.data?.existingOrSimilar?.contactPersonList || [])})
            </p>
            {modalState.data?.existingOrSimilar?.status === 0 && (
              <p style={{ marginTop: 4, fontWeight: 'bold', textAlign: 'center', color: '#ff4d4f' }}>
                INACTIVE ADDRESS!!!
              </p>
            )}
            <p style={{ marginTop: 8, fontWeight: 'bold', textAlign: 'center' }}>Should we still continue to add cooperative warehouses?</p>
          </div>
        </Modal>
      )}
    </>
  );
};

export default CooperationWarehouseFormDrawer;